import {
  Bindings,
  ConditionalExpression,
  EvaluateableExpression,
  Let,
  OPERATORS,
} from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/condition';
import {
  Literal,
  Value,
  VariableReference,
} from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/data-reference';
import {
  TraceMessageType,
  TraceState,
} from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/trace';
import { JSONPath } from 'jsonpath-plus';
import {
  CachedBindings,
  DataReferenceResolver,
  defaultDataReferenceResolver,
  isVoid,
  PartialEvaluation,
  QueryResolver,
} from './data-reference-resolver';
import { Either, isLeft, isRight, left, Right, right } from './either';
import { isDataReference, isEvaluateableExpression, isLet, isVariableReference } from './guards';
import { arityCheck, typeCheck } from './signatures';
import { createTraceMessageEntry, tracingEnabled } from './trace-utils';

// from: https://stackoverflow.com/a/39419171
function assertUnreachable(x: never): never {
  throw new Error("Didn't expect to get here");
}

/*
  The output of operators / conjunctions is one of 3 possible values:
  * true
  * false
  * uneval (aka partially evaluated) - if we're on the client, this means send to server for evaluation.

  == Operators ==
  Generally, relation operators (<>=) require all operands to be evaluated for evaluation
  (otherwise the entire assertion is unevaluated).

  EQ        1         2         uneval
  1         true      false     uneval
  2         false     true      uneval
  uneval    uneval    uneval    uneval

  == Conjunctions ==


  AND       true      false     uneval
  true      true      false     uneval
  false     false     false     false
  uneval    uneval    false     uneval

  OR        true      false     uneval
  true      true      true      true
  false     true      false     uneval
  uneval    true      uneval    uneval

  NOT       true      false     uneval
            false     true      uneval
*/

export const isTruthy = (value: any) => {
  // make any empty array falsey
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return !!value;
};

const intersection = (a: Literal[], b: Literal[]): Literal[] => {
  return a.filter(value => b.includes(value));
};

// from https://stackoverflow.com/a/63497965
async function generatorToArray<T>(gen: AsyncGenerator<T, void, unknown>): Promise<Array<T>> {
  const out: T[] = [];
  for await (const x of gen) {
    out.push(x);
  }
  return out;
}

function allArgsLiteral(args: Either<ConditionalExpression, Literal>[]): args is Right<Literal>[] {
  return args.every(isRight);
}

const unpackLiterals = (args: Right<Literal>[]) => args.map(a => a.value);
const unpackValues = (args: Either<ConditionalExpression, Literal>[]) => args.map(a => a.value);

type ArgGenerator = AsyncGenerator<Either<ConditionalExpression, Literal>, void, unknown>;

const wrapWithTrace = <
  TAILARGS extends any[],
  F extends (traceState: TraceState | undefined, ...args: TAILARGS) => PartialEvaluation
>(
  op: OPERATORS,
  fn: F
) => async (traceState: TraceState | undefined, ...tail: TAILARGS): PartialEvaluation => {
  createTraceMessageEntry(traceState, () => ({
    type: TraceMessageType.QCE_OP_CALL,
    op,
  }));
  try {
    const result = await fn(traceState, ...tail);
    if (isRight(result)) {
      createTraceMessageEntry(traceState, () => ({
        type: TraceMessageType.QCE_OP_RETURN,
        op,
        result: result.value,
      }));
    }
    if (isLeft(result)) {
      createTraceMessageEntry(traceState, () => ({
        type: TraceMessageType.QCE_OP_RETURN,
        op,
        partialResult: result.value,
      }));
    }
    return result;
  } catch (e) {
    createTraceMessageEntry(traceState, () => ({
      type: TraceMessageType.QCE_OP_RETURN,
      op,
      error: {
        message: e.message,
        stack: e.stack,
      },
    }));
    throw e;
  }
};

/**
 * When an operator cannot be partially evaluated (it can only be evaluated once)
 * all arguments have been evaluated, then getGenericEvaluator can be used to
 * abstract away everything except the actual function application.
 * @param op The operator
 * @param fn The function to apply
 * @returns Result of function application, must be a Literal
 */
const getGenericEvaluator = (
  op: OPERATORS,
  fn: (traceState: TraceState | undefined, ...args: Literal[]) => Literal
): ((traceState: TraceState | undefined, argIterator: ArgGenerator) => PartialEvaluation) =>
  wrapWithTrace(
    op,
    async (traceState: TraceState | undefined, argIterator: ArgGenerator): PartialEvaluation => {
      const allArgs = await generatorToArray(argIterator);
      if (allArgsLiteral(allArgs)) {
        const args = unpackLiterals(allArgs);
        typeCheck(op, args);
        return right(fn(traceState, ...args));
      }
      return left([op, ...unpackValues(allArgs)]);
    }
  );

const evalAnd = wrapWithTrace(
  OPERATORS.AND,
  async (traceState: TraceState | undefined, argIterator: ArgGenerator): PartialEvaluation => {
    // read all arguments, eagerly return false if any are false:
    const allArgs: Array<Either<ConditionalExpression, Literal>> = [];
    // read arguments performing short-circuit evaluation
    for await (const arg of argIterator) {
      if (isRight(arg) && !isTruthy(arg.value)) {
        return right(false);
      }
      allArgs.push(arg);
    }
    // if all of them have been evaluated to true, result is true:
    if (allArgs.every(a => isRight(a) && isTruthy(a.value))) {
      return right(true);
    }
    // at this point only truthy and unevaluated args remain,
    // so the unevaluated args determine the outcome of the expression
    let unevaluated = allArgs.filter(a => isLeft(a)).map(a => a.value);
    // if only a single unevaluated arg remains, remove AND
    if (unevaluated.length === 1) {
      return left(unevaluated[0]);
    }
    // otherwise, only those not yet evaluated need to be returned
    return left([OPERATORS.AND, ...unevaluated]);
  }
);

const evalIf = wrapWithTrace(
  OPERATORS.IF,
  async (
    traceState: TraceState | undefined,
    args: ConditionalExpression[],
    evaluateArg: (expr: ConditionalExpression) => PartialEvaluation
  ): PartialEvaluation => {
    const conditionResult = await evaluateArg(args[0]);
    if (isRight(conditionResult)) {
      // depending on condition, choose either second or third argument as result
      return evaluateArg(args[isTruthy(conditionResult.value) ? 1 : 2]);
    }
    // conditionResult.value may be partially evaluated
    return left(
      [OPERATORS.IF, conditionResult.value].concat(args.slice(1)) as ConditionalExpression
    );
  }
);

const evalOr = wrapWithTrace(
  OPERATORS.OR,
  async (traceState: TraceState | undefined, argIterator: ArgGenerator): PartialEvaluation => {
    const allArgs: Array<Either<ConditionalExpression, Literal>> = [];
    // read all arguments, eagerly return true if any are true:
    for await (const arg of argIterator) {
      if (isRight(arg) && isTruthy(arg.value)) {
        return right(true);
      }
      allArgs.push(arg);
    }
    // if all of the have been evaluated to false, result is false:
    if (allArgs.every(a => isRight(a) && !isTruthy(a.value))) {
      return right(false);
    }
    // at this point only falsey and unevaluated args remain,
    // so the unevaluated args determine the outcome of the expression
    let unevaluated = allArgs.filter(a => isLeft(a)).map(a => a.value);
    // if only a single unevaluated arg remains, remove OR
    if (unevaluated.length === 1) {
      return left(unevaluated[0]);
    }
    // otherwise, only those not yet evaluated need to be returned
    return left([OPERATORS.OR, ...unevaluated]);
  }
);

/**
 * evalTrace is a little tricky.
 * Every operator *must* evaluate its arguments, currently there isn't an easy way
 * to pass on arguments unevaluated (because it needs to return a PartialEvaluation).
 * So evalTrace evaluates it's first argument and returns the result.
 * The trace message of all arguments (including the first) contains the raw, unevaluated version.
 */
const evalTrace = async (
  traceState: TraceState | undefined,
  args: ConditionalExpression[],
  evaluateArg: (expr: ConditionalExpression) => PartialEvaluation
): PartialEvaluation => {
  createTraceMessageEntry(traceState, {
    type: TraceMessageType.QCE_OP_TRACE,
    evaluated: false,
    args,
  });
  // evaluate first argument as result of entire trace expression
  return evaluateArg(args[0]);
};

const evalTraceEval = async (
  traceState: TraceState | undefined,
  argIterator: ArgGenerator
): PartialEvaluation => {
  const allArgs: Array<Either<ConditionalExpression, Literal>> = [];
  for await (const arg of argIterator) {
    allArgs.push(arg);
  }
  createTraceMessageEntry(traceState, {
    type: TraceMessageType.QCE_OP_TRACE,
    evaluated: true,
    args: allArgs.map(a => a.value),
  });
  return allArgs[0];
};

const evalNot = wrapWithTrace(
  OPERATORS.NOT,
  async (traceState: TraceState | undefined, argIterator: ArgGenerator): PartialEvaluation => {
    const allArgs: Array<Either<ConditionalExpression, Literal>> = [];
    for await (const arg of argIterator) {
      allArgs.push(arg);
    }
    if (isLeft(allArgs[0])) {
      // if arg cannot be evaluated, !arg cannot yet either.
      return left([OPERATORS.NOT, allArgs[0].value]);
    }
    return right(!isTruthy(allArgs[0].value));
  }
);

const evalEqual = wrapWithTrace(
  OPERATORS.EQ,
  async (traceState: TraceState | undefined, argIterator: ArgGenerator): PartialEvaluation => {
    const literalSet: Set<Literal> = new Set();
    const unevaluated: ConditionalExpression[] = [];
    for await (const arg of argIterator) {
      if (isRight(arg)) {
        // if we've already seen a literal and this is a different one, we
        // know the result is false.
        if (literalSet.size > 0 && !literalSet.has(arg.value)) {
          return right(false);
        }
        literalSet.add(arg.value);
      } else {
        unevaluated.push(arg.value);
      }
    }
    // By this point if there were multiple different literals, we would have returned false.
    // So any code from here can assume that literaSet.size is [0,1].

    // if all the arguments are literal, and they're all the same value, return true
    if (unevaluated.length < 1) {
      return right(true);
    }
    // Any duplicates can be thrown out, they have no impact on the final result.
    // The unevaluated arguments are returned along with the deduplicated literal value they must be equal to.
    return left([
      OPERATORS.EQ,
      ...Array.from(literalSet),
      ...unevaluated,
    ] as EvaluateableExpression);
  }
);

const evalLessThan = getGenericEvaluator(
  OPERATORS.LT,
  (traceState: TraceState | undefined, a: Literal, b: Literal) => a! < b!
);

const evalGreaterThan = getGenericEvaluator(
  OPERATORS.GT,
  (traceState: TraceState | undefined, a: Literal, b: Literal) => a! > b!
);

const evalMinus = getGenericEvaluator(
  OPERATORS.MINUS,
  (traceState: TraceState | undefined, a: Literal, b?: Literal) =>
    isVoid(b) ? -1 * (a as number) : (a as number) - (b as number)
);

const evalDivide = getGenericEvaluator(
  OPERATORS.DIVIDE,
  (traceState: TraceState | undefined, a: Literal, b: Literal) => (a as number) / (b as number)
);

const evalRegexp = getGenericEvaluator(
  OPERATORS.REGEXP,
  (traceState: TraceState | undefined, pattern: Literal, str: Literal, opts?: Literal) => {
    const re = opts ? new RegExp(pattern as string, opts as string) : new RegExp(pattern as string);
    return str !== null && typeof str !== 'undefined' && (str as string).match(re) != null;
  }
);

const evalIn = getGenericEvaluator(
  OPERATORS.IN,
  (traceState: TraceState | undefined, needle: Literal, haystack: Literal) =>
    typeof needle !== 'undefined' && (haystack as Literal[]).includes(needle)
);

const evalPath = getGenericEvaluator(
  OPERATORS.PATH,
  (traceState: TraceState | undefined, path: Literal, data: Literal, fallback?: Literal) => {
    let [json] = JSONPath({ path: path as string, json: data as any });
    if (isVoid(json) && !isVoid(fallback)) {
      json = fallback;
    }
    // undefined cannot be JSON serialized, so replace with null if encountered
    return isVoid(json) ? null : json;
  }
);

const evalIntersect = getGenericEvaluator(
  OPERATORS.INTERSECT,
  (traceState: TraceState | undefined, setA: Literal, setB: Literal) =>
    intersection(setA as Literal[], setB as Literal[])
);

const evalSum = wrapWithTrace(
  OPERATORS.SUM,
  async (traceState: TraceState | undefined, argIterator: ArgGenerator): PartialEvaluation => {
    const unevaluated: ConditionalExpression[] = [];
    let sum = 0;
    for await (const arg of argIterator) {
      if (isRight(arg)) {
        sum += arg.value as number;
      } else {
        unevaluated.push(arg.value);
      }
    }
    if (unevaluated.length > 0) {
      if (sum === 0 && unevaluated.length === 1) {
        return left(unevaluated[0]);
      }
      return left(
        ([OPERATORS.SUM] as any[]).concat(
          sum === 0 ? [] : sum,
          unevaluated
        ) as ConditionalExpression
      );
    }
    return right(sum);
  }
);

const evalProduct = wrapWithTrace(
  OPERATORS.PRODUCT,
  async (traceState: TraceState | undefined, argIterator: ArgGenerator): PartialEvaluation => {
    const unevaluated: ConditionalExpression[] = [];
    let product = 1;
    for await (const arg of argIterator) {
      if (isRight(arg)) {
        product = product * (arg.value as number);
      } else {
        unevaluated.push(arg.value);
      }
    }
    if (unevaluated.length > 0) {
      if (product === 1 && unevaluated.length === 1) {
        return left(unevaluated[0]);
      }
      return left(
        ([OPERATORS.PRODUCT] as any[]).concat(
          product === 1 ? [] : product,
          unevaluated
        ) as ConditionalExpression
      );
    }
    return right(product);
  }
);

const evaluateExpression = async (
  evaluateableExpression: EvaluateableExpression,
  resolver: DataReferenceResolver,
  bindingsStack: CachedBindings[],
  traceState?: TraceState
): PartialEvaluation => {
  const [head, ...tail] = evaluateableExpression;
  const opTraceState =
    traceState && tracingEnabled(traceState, 'tracedOperators', head) ? traceState : undefined;
  const evaluateArg = (arg: ConditionalExpression) =>
    evaluateInternal(arg, resolver, bindingsStack, traceState);
  arityCheck(head, tail);
  // evaluation of arguments is sequential so lazy evaluation works
  const argIterator = (async function* () {
    for (let arg of tail) {
      yield await evaluateArg(arg);
    }
  })();
  switch (head) {
    case OPERATORS.AND:
      return evalAnd(opTraceState, argIterator);
    case OPERATORS.OR:
      return evalOr(opTraceState, argIterator);
    case OPERATORS.NOT:
      return evalNot(opTraceState, argIterator);
    case OPERATORS.LT:
      return evalLessThan(opTraceState, argIterator);
    case OPERATORS.GT:
      return evalGreaterThan(opTraceState, argIterator);
    case OPERATORS.REGEXP:
      return evalRegexp(opTraceState, argIterator);
    case OPERATORS.EQ:
      return evalEqual(opTraceState, argIterator);
    case OPERATORS.IN:
      return evalIn(opTraceState, argIterator);
    case OPERATORS.IF:
      return evalIf(opTraceState, tail, evaluateArg);
    case OPERATORS.INTERSECT:
      return evalIntersect(opTraceState, argIterator);
    case OPERATORS.SUM:
      return evalSum(opTraceState, argIterator);
    case OPERATORS.PATH:
      return evalPath(opTraceState, argIterator);
    case OPERATORS.MINUS:
      return evalMinus(opTraceState, argIterator);
    case OPERATORS.PRODUCT:
      return evalProduct(opTraceState, argIterator);
    case OPERATORS.DIVIDE:
      return evalDivide(opTraceState, argIterator);
    case OPERATORS.TRACE:
      return evalTrace(opTraceState, tail, evaluateArg);
    case OPERATORS.TRACE_EVAL:
      return evalTraceEval(opTraceState, argIterator);
    default:
      assertUnreachable(head);
  }
};

/*
  When a variable is attempted to be resolved, the following can happen:
  * No such variable binding in any of the bindings on the stack -> error
  * A binding is declared on the stack, but no cached value exists yet.
  * A binding is declared on the stack and there is a cached value which is a pending promise
  * A binding is declared on the stack and there is a cached value which is a resolved promise
  For our purposes, the last two are identical, so we have 3 cases.
 */
const resolveVariableReference = async (
  ref: VariableReference,
  resolve: DataReferenceResolver,
  bindingsStack: CachedBindings[],
  traceState?: TraceState
): PartialEvaluation => {
  const variableName = ref.variable;
  // search for a binding in the stack elements newest to oldest
  for (let i = bindingsStack.length - 1; i > -1; i--) {
    if (variableName in bindingsStack[i].bindings) {
      if (variableName in bindingsStack[i].cache) {
        // if we already have a cached value, return
        // cache value if it's right()
        if (isRight(await bindingsStack[i].cache[variableName])) {
          return bindingsStack[i].cache[variableName];
        }
        // return the original variable reference if it's left()
        return left(ref);
      } else {
        const variableDefinition = bindingsStack[i].bindings[variableName];
        if (isVariableReference(variableDefinition)) {
          throw new Error(
            `Variable value cannot be reference to another variable (var ${ref.variable} refers to var ${variableDefinition.variable})`
          );
        }
        // set cached value
        const value = evaluateInternal(
          variableDefinition,
          resolve,
          // variable bindings up to (not including) the Let expression are visible in the current scope
          bindingsStack.slice(0, i),
          traceState
        );
        bindingsStack[i].cache[variableName] = value;
        // if the value resolves to a literal replace the var ref:
        const resolvedValue = await value;
        if (isRight(resolvedValue)) {
          return resolvedValue;
        }
        // otherwise keep the reference as is:
        return left(ref);
      }
    }
  }
  throw new Error(`Variable ${variableName} referenced but not bound.`);
};

const evaluateValue = async (
  value: Value,
  resolve: DataReferenceResolver,
  bindingsStack: CachedBindings[],
  traceState?: TraceState
): PartialEvaluation => {
  if (isDataReference(value)) {
    const resolution = await resolve(value, bindingsStack, traceState);
    if (isRight(resolution)) {
      // data reference should not resolve to a variable, only Literal or DataReference if its unresolvable
      if (isVariableReference(resolution.value)) {
        throw new Error(
          `DataReference shouldn't resolve to variable reference (var: ${resolution.value.variable})`
        );
      }
      // if resolution isRight, then its value must be a literal
      return resolution as Right<Literal>;
    } else {
      // data reference couldn't be resolved
      return left(resolution.value);
    }
  } else {
    // value wasn't a datareference
    if (isVariableReference(value)) {
      // if it's a variable reference, resolve the variable:
      return resolveVariableReference(value, resolve, bindingsStack, traceState);
    }
    // simply return the value if its a literal
    return right(value);
  }
};

const getPartiallyEvaluatedBindings = (cachedBindings: CachedBindings): Promise<Bindings> =>
  Object.keys(cachedBindings.bindings).reduce(
    async (bindingsPromise: Promise<Bindings>, varName: string): Promise<Bindings> => {
      // Expressions within the body of the Let can reference any binding.
      // As a result, we can't get rid of any bindings
      // - If there is a cached evaluated version of the binding's value then use the
      //   cached version in binding to partially evaluated resulting let
      // - If no cached version, then an unevaluated Let in nested in the body may be referencing
      //   the value, so pass it on
      const bindings = await bindingsPromise;
      if (varName in cachedBindings.cache) {
        const cachedBindingValue = await cachedBindings.cache[varName];
        bindings[varName] = cachedBindingValue.value;
      } else {
        bindings[varName] = cachedBindings.bindings[varName];
      }
      return bindings;
    },
    Promise.resolve({}) as Promise<Bindings>
  );

const evaluateLet = async (
  letExpression: Let,
  resolve: DataReferenceResolver,
  bindingsStack: CachedBindings[],
  traceState?: TraceState
): PartialEvaluation => {
  const currentCachedBindings = {
    bindings: letExpression.bindings,
    cache: {},
  };
  const result = await evaluateInternal(
    letExpression.body,
    resolve,
    bindingsStack.concat(currentCachedBindings),
    traceState
  );
  if (isLeft(result)) {
    return left({
      bindings: await getPartiallyEvaluatedBindings(currentCachedBindings),
      body: result.value,
    });
  }
  return result;
};

export const evaluateInternal = async (
  conditionalExpression: ConditionalExpression,
  resolve: DataReferenceResolver,
  bindingsStack: CachedBindings[] = [],
  traceState?: TraceState
): PartialEvaluation => {
  if (isEvaluateableExpression(conditionalExpression)) {
    return evaluateExpression(conditionalExpression, resolve, bindingsStack, traceState);
  } else if (isLet(conditionalExpression)) {
    return evaluateLet(conditionalExpression, resolve, bindingsStack, traceState);
  } else {
    return evaluateValue(conditionalExpression, resolve, bindingsStack, traceState);
  }
};
/**
 * Evaluates a conditional expression.
 * @param conditionalExpression
 * @param queryResolver
 */

export const evaluate = async (
  conditionalExpression: ConditionalExpression,
  queryResolver: QueryResolver,
  bindingsStack: CachedBindings[] = [],
  traceState?: TraceState
): PartialEvaluation => {
  return await evaluateInternal(
    conditionalExpression,
    defaultDataReferenceResolver(queryResolver),
    bindingsStack,
    traceState
  );
};
